import './symptoms.scss';
import './types-of-utuc.scss';
import React, { FC, useEffect, useRef, useState } from 'react';
import { navigate } from '@reach/router';
import classnames from 'classnames';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import iconArrows from '../../images/icon-arrows.png';
import iconMarker from '../../images/icon-marker.png';
import iconTumor from '../../images/icon-tumor.png';
import iconCheckmark from '../../images/icon-checkmark.svg';
import { getParam } from '../utils';
import tw, { css, styled } from 'twin.macro';
import Definition from '../definition/definition';
import ButtonRound from '../buttonRound';

type UnderstandingTreatmentParams = {
  location: Location;
  activeSection;
  onLoadinComplete?: () => {};
};

const UnderstandingTreatment: FC<UnderstandingTreatmentParams> = ({
  location,
  activeSection,
  onLoadinComplete,
}) => {
  const knownTabs = ['grade', 'stage', 'tumor'];
  const isKnownTab = (t: string): t is typeof knownTabs[number] => {
    return knownTabs.includes(t.substring(1));
  };
  const [activeTab, setActiveTab] = useState<typeof knownTabs[number]>(
    knownTabs[0],
  );
  const tabParam = 'causes'; //the ?[tabParam='paramName'] part of the url

  useEffect(() => {
    // if (location.hash && isKnownTab(location.hash)) {
    //   const tab = getParam(location.hash, knownTabs);
    //   if (tab && activeTab !== tab) {
    //     setActiveTab(tab);
    //   }
    // }
    if (location.hash) {
      const tab = getParam(location.hash, knownTabs);
      if (tab && activeTab !== tab) {
        setActiveTab(tab);
      }
    }
  }, [location.hash]);

  return (
    <section
      className="subsection understanding__subsection"
      data-id="types"
      aria-label="Types of UTUC"
      tw="md:pr-0!"
    >
      <div className="treatment__inner">
        <div
          className="treatment__content_container"
          tw="md:(width[calc(100% - 135px)]!)"
        >
          <h3 className="treatment__title">Low risk and high risk</h3>
          <div className="treatment__content">
            <p>
              Your <Definition term="urologist" id="urologist" /> probably
              performed a <Definition term="biopsy" id="biopsy" /> as part of
              your cancer diagnosis. A biopsy is a medical test that involves a
              minor operation to take some tissue and cells to be studied. It
              gives your doctor more information about your cancer.
            </p>
            <p>
              Before you choose a treatment, your doctor will usually look at
              the results of your biopsy and talk to you about risk. Urologists
              think about UTUC as <Definition term="low risk" id="low_risk" />
              &nbsp;or <Definition term="high risk" id="high_risk" />, depending
              on how dangerous they think it is. Low-risk UTUC is not considered
              as dangerous as high-risk UTUC. Risk is often determined by the{' '}
              <Definition term="grade" id="grade" /> and{' '}
              <Definition term="stage" id="stage" /> of your cancer, as well as
              the type of <Definition term="tumor" id="tumor" />
              (s) that you have. The tabs below give additional information on
              these subjects.
            </p>
            <div style={{ marginTop: '34px' }}>
              <div
                className="nav-and-tabs"
                style={{ marginTop: '30px' }}
                data-id="causes"
              >
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === knownTabs[0],
                      })}
                      onClick={() => {
                        navigate(
                          `${activeSection}?${tabParam}=${knownTabs[0]}`,
                        );
                      }}
                      tw="border-top-left-radius[.5rem]! border-top-right-radius[.5rem]!"
                    >
                      <div className="nav-and-tabs__link_content">
                        <img
                          className="nav-and-tabs__nav_icon"
                          data-id="grade"
                          src={iconArrows}
                          alt="Icon of Arrows Pointing Upward and Downward"
                        />
                        Grade
                      </div>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === knownTabs[1],
                      })}
                      onClick={() => {
                        navigate(
                          `${activeSection}?${tabParam}=${knownTabs[1]}`,
                        );
                      }}
                      tw="border-top-left-radius[.5rem]! border-top-right-radius[.5rem]!"
                    >
                      <div className="nav-and-tabs__link_content">
                        <img
                          className="nav-and-tabs__nav_icon"
                          data-id="stage"
                          src={iconMarker}
                          alt="Icon of a marker between two points of a line"
                        />
                        Stage
                      </div>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === knownTabs[2],
                      })}
                      onClick={() => {
                        navigate(
                          `${activeSection}?${tabParam}=${knownTabs[2]}`,
                        );
                      }}
                      tw="border-top-left-radius[.5rem]! border-top-right-radius[.5rem]!"
                    >
                      <div className="nav-and-tabs__link_content">
                        <img
                          className="nav-and-tabs__nav_icon"
                          data-id="tumor"
                          src={iconTumor}
                          alt="Icon of a tumor"
                        />
                        Tumor
                      </div>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId={knownTabs[0]}>
                    <div className="tab-pane__content" data-id={knownTabs[0]}>
                      <p>
                        In UTUC, the cancer{' '}
                        <Definition term="grade" id="grade" /> is either low
                        grade or high grade. There is no medium grade in UTUC.
                      </p>
                      <p>
                        The chart below shows the main difference between the
                        two grades. While high-grade UTUC is more likely to be
                        aggressive and to spread, low-grade UTUC has a risk of
                        recurring.
                      </p>
                      <div>
                        {/* Phase 2 WIP TODO: Rounded td corners */}
                        <TypeTable>
                          <TableRow>
                            <TableHead tw="border-width[0 0 1px 0] border-brand-blue-light"></TableHead>
                            <TableHead tw="border-width[2px 1px 1px 2px] border-brand-blue-light">
                              Low-grade UTUC
                            </TableHead>
                            <TableHead tw="border-width[2px 2px 1px 1px] border-brand-blue-light">
                              High-grade UTUC
                            </TableHead>
                            <TableHead tw="border-none"></TableHead>
                          </TableRow>
                          <TableRow>
                            <TableData tw="font-semibold border-width[1px 1px 1px 0] border-brand-blue-light text-right">
                              Aggressive <MobileBreak /> (likely to spread)
                            </TableData>
                            <TableData tw="border-width[1px 1px 1px 1px] border-brand-blue-light"></TableData>
                            <TableData tw="whitespace-nowrap border-width[1px 0 1px 1px] border-brand-blue-light">
                              <CheckMark src={iconCheckmark} alt="" />
                            </TableData>
                            <TableData tw="whitespace-nowrap border-none"></TableData>
                          </TableRow>
                          <TableRow>
                            <TableData tw="font-semibold border-width[1px 1px 1px 0] border-brand-blue-light text-right">
                              Invasive <MobileBreak />
                              (likely to grow into <DeskBreak />
                              surrounding tissue)
                            </TableData>
                            <TableData tw="border-width[1px 1px 1px 1px] border-brand-blue-light"></TableData>
                            <TableData tw="whitespace-nowrap border-width[1px 0 1px 1px] border-brand-blue-light">
                              <CheckMark src={iconCheckmark} alt="" />
                            </TableData>
                            <TableData tw="whitespace-nowrap border-none"></TableData>
                          </TableRow>
                          <TableRow>
                            <TableData tw="font-semibold border-width[1px 1px 2px 0] border-brand-blue-light text-right">
                              Likely to recur
                            </TableData>
                            <TableData tw="border-width[1px 1px 2px 1px] border-brand-blue-light">
                              <CheckMark src={iconCheckmark} alt="" />
                            </TableData>
                            <TableData tw="whitespace-nowrap border-width[1px 0 2px 1px] border-brand-blue-light flex flex-col items-center gap-2 md:table-cell">
                              <CheckMark src={iconCheckmark} alt="" />
                              <span tw="inline md:hidden">
                                (depending on
                                <br />
                                treatment)
                              </span>
                            </TableData>
                            <TableData tw="whitespace-nowrap border-none text-left text-xs hidden md:table-cell">
                              (depending on
                              <br />
                              treatment)
                            </TableData>
                          </TableRow>
                        </TypeTable>
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={knownTabs[1]}>
                    <div className="tab-pane__content" data-id={knownTabs[1]}>
                      <p>
                        The <Definition term="stage" id="stage" /> of your
                        cancer depends on how deep your cancer has grown into
                        the organ tissue.
                      </p>
                      <p>
                        The stage could be anywhere from 0 to 4. Stage 0 UTUC is
                        right on the surface of your{' '}
                        <Definition term="urothelium" id="urothelium" />. Stage
                        4 UTUC has grown deep into your kidney or{' '}
                        <Definition term="ureter" id="ureter" /> and has also
                        spread to another part of your body.
                      </p>
                      <div tw="flex flex-col gap-8 mt-4 md:flex-row">
                        <p tw="mt-0!">
                          It can be hard to know what stage UTUC is. Your
                          urologist might not be able to tell from your biopsy.
                        </p>
                        <ButtonRound
                          styleName="treatment__button button-and-title__button"
                          linkText="See your treatment options"
                          link="/treatment-options#choosing_a_treatment"
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={knownTabs[2]}>
                    <div className="tab-pane__content" data-id={knownTabs[2]}>
                      <p>
                        Tumor characteristics help inform whether your UTUC is
                        low risk or high risk and also help determine the best
                        treatment approach.
                      </p>
                      <ul className="bullet-list" tw="pl-4">
                        <li className="bullet-list__item" tw="my-4">
                          <div>
                            <span tw="font-bold">Size</span>: Larger tumors are
                            usually higher risk than smaller tumors
                          </div>
                        </li>
                        <li className="bullet-list__item" tw="my-4">
                          <div>
                            <span tw="font-bold">Location</span>: Tumors in the
                            large, open part of the kidneys (the renal pelvis)
                            are more easily removed, while tumors in the small
                            parts of the kidneys (the calyces [KAY-lih-sees])
                            can be harder to reach and remove
                          </div>
                        </li>
                        <li className="bullet-list__item" tw="my-4">
                          <div tw="flex flex-col gap-8 justify-end md:flex-row">
                            <div>
                              <strong tw="font-bold">Number of tumors</strong>:
                              The more tumors there are, the harder it may be to
                              remove them all
                            </div>
                            <ButtonRound
                              styleName="treatment__button button-and-title__button"
                              linkText="See your treatment options"
                              link="/treatment-options#choosing_a_treatment"
                            />
                          </div>
                        </li>
                      </ul>
                    </div>
                  </TabPane>
                </TabContent>
              </div>
              {activeTab === knownTabs[0] && (
                <div tw="flex flex-col gap-8 mt-8 items-center md:flex-row">
                  <h4 tw="text-brand-blue-darker font-normal font-size[22px]">
                    UTUC grade is a key factor in choosing the best treatment
                    option.
                  </h4>
                  <ButtonRound
                    styleName="treatment__button button-and-title__button"
                    linkText="See your treatment options"
                    link="/treatment-options#choosing_a_treatment"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnderstandingTreatment;
const TypeTable = tw.table`line-height[1.2] border-separate border-spacing[0] m-4 md:(my-8)`;
const TableRow = tw.tr`color[#064B74] font-semibold text-center border[2px solid #95B9C7] border-radius[10px 10px 0 0]`;
const TableHead = tw.th`p-2 font-semibold text-center border[2px solid #95B9C7] border-radius[10px 10px 0 0]
  md:(py-4 px-6 font-size[22px])`;
const TableData = tw.td`p-2 border[1px solid #95B9C7] font-normal text-sm
  md:(py-4 px-6 text-xl)`;
const CheckMark = tw.img`h-6 w-6 md:(h-8 w-8)`;
const DeskBreak = tw.br`hidden md:block`;
const MobileBreak = tw.br`block md:hidden`;
